import React, { useState, useEffect, useRef } from "react";
import { useTimezoneSelect, allTimezones } from "react-timezone-select";
import { SketchPicker } from "react-color";
import Draggable from "react-draggable";

export default function ClockModal({
  id,
  patchSettingsToBackend2,
  clockData,
  componentInfo,
  deleteData,
  componentName
}) {
  const [selectedTimezone, setSelectedTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const labelStyle = "offsetHidden";
  const timezones = {
    ...allTimezones,
  };
  const { options, parseTimezone } = useTimezoneSelect({
    labelStyle,
    timezones,
  });
  const handleTimezoneChange = (timezoneValue) => {
    const parsedTimezone = parseTimezone(timezoneValue);
    setSelectedTimezone(parsedTimezone);
  };
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [settings, setSettings] = useState({});
  const [isDraggable, setIsDraggable] = useState(true);
  const [timeouts, setTimeouts] = useState({
    height: null,
    width: null,
    xPosition: null,
    yPosition: null,
    opacity: null,
    cityTimeText: null,
    cityNameText: null,
    zIndex: null,
    label: null,
  });
  const nodeRef = useRef(null);
  useEffect(() => {
    if (componentInfo) {
      setSettings({
        cityNameText: componentInfo.cityNameText,
        cityTimeText: componentInfo.cityTimeText,
        backgroundColor: componentInfo.clockContainerBackgroundColor,
        textColor: componentInfo.clockTextColor,
        opacity: componentInfo.opacity,
        width: componentInfo.width,
        height: componentInfo.height,
        xPosition: componentInfo.xPosition,
        yPosition: componentInfo.yPosition,
        clockBackgroundColor: componentInfo.clockBackgroundColor,
        zIndex: componentInfo.zIndex,
        showCity: componentInfo.showCity,
        label: componentInfo.label,
      });
    }
  }, [componentInfo]);

  const updateTimeout = (key, value) => {
    setTimeouts((prevTimeouts) => ({
      ...prevTimeouts,
      [key]: value,
    }));
  };

  useEffect(() => {
    return () => {
      Object.values(timeouts).forEach((timeout) => clearTimeout(timeout));
    };
  }, [timeouts]);

  const addClock = () => {
    const updatedTimezones = [...componentInfo.timeZones, selectedTimezone];
    patchSettingsToBackend2(
      {
        timeZones: updatedTimezones,
      },
      clockData.SettingsJson,
      id
    );
  };

  const removeTimezone = (timezoneIndex) => {
    const updatedTimezones = [...componentInfo.timeZones];
    updatedTimezones.splice(timezoneIndex, 1);
    patchSettingsToBackend2(
      {
        timeZones: updatedTimezones,
      },
      clockData.SettingsJson,
      id
    );
  };

  const handleOnBackgroundChange = (color) => {
    const newColor = color.hex;
    setSettings({ ...settings, backgroundColor: newColor });
    patchSettingsToBackend2(
      {
        clockBackgroundColor: newColor,
      },
      clockData.SettingsJson,
      id
    );
  };

  const handleOnTextChange = (color) => {
    const newColor = color.hex;
    setSettings({ ...settings, textColor: newColor });
    patchSettingsToBackend2(
      {
        clockTextColor: newColor,
      },
      clockData.SettingsJson,
      id
    );
  };

  const handleSettingChange = (key, value) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [key]: value,
    }));
    clearTimeout(timeouts[key]);
    updateTimeout(
      key,
      setTimeout(() => {
        patchSettingsToBackend2({ [key]: value }, clockData.SettingsJson, id);
      }, 500)
    );
  };

  const handleInputChange = (key, value, maxLimit) => {
    let parsedValue = parseInt(value, 10);
    if (isNaN(parsedValue)) {
      parsedValue = 2;
    } else if (parsedValue > maxLimit) {
      parsedValue = maxLimit;
    }
    handleSettingChange(key, parsedValue);
  };

  return (
    <Draggable nodeRef={nodeRef} disabled={!isDraggable}>
      <div
        className="modal "
        id={`clockModal${id}`}
        tabIndex="-1"
        aria-labelledby={`clockModal${id}`}
        aria-hidden="true"
        data-bs-backdrop="static"
        ref={nodeRef}
      >
        <div className="modal-dialog">
          <div className="modal-content rounded-0">
            <div className="modal-header">
              <h4 className="modal-title fs-5" id={`clockModal${id}`}>
                Properties for {componentName}
              </h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div
              className="modal-body"
              onMouseEnter={() => setIsDraggable(false)}
              onMouseLeave={() => setIsDraggable(true)}
            >
              <>
                <ol className="list-group list-group-flush text-start">
                  <li className="list-group-item d-flex justify-content-between align-items-start">
                    <div className=" me-auto">Opacity</div>
                    <span className="me-2">{settings.opacity}%</span>
                    <span className="">
                      <input
                        type="range"
                        className="form-range"
                        min="0"
                        max="100"
                        step="25"
                        id="customRangeClock3"
                        value={settings.opacity || 0}
                        onChange={(e) =>
                          handleSettingChange(
                            "opacity",
                            parseInt(e.target.value)
                          )
                        }
                      />
                    </span>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-start w-100">
                    <div className="row w-100">
                      <div className="col input-group">
                        <span
                          className="input-group-text border-end-0"
                          id="width"
                        >
                          width
                        </span>
                        <input
                          name="width"
                          type="number"
                          min={2}
                          max={24 - settings.xPosition || 0}
                          className="form-control"
                          aria-label="width"
                          aria-describedby="width"
                          onChange={(e) =>
                            handleInputChange(
                              "width",
                              e.target.value,
                              24 - settings.xPosition
                            )
                          }
                          value={settings.width || ""}
                        />
                      </div>
                      <div className="col input-group">
                        <span className="input-group-text" id="height">
                          height
                        </span>
                        <input
                          name="height"
                          type="number"
                          min={2}
                          max={24 - settings.yPosition || 0}
                          className="form-control"
                          aria-label="height"
                          aria-describedby="height"
                          onChange={(e) =>
                            handleInputChange(
                              "height",
                              e.target.value,
                              24 - settings.yPosition
                            )
                          }
                          value={settings.height || ""}
                        />
                      </div>
                    </div>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-start">
                    <div className="row w-100">
                      <div className="col input-group">
                        <span className="input-group-text" id="X">
                          X
                        </span>
                        <input
                          name="X"
                          type="number"
                          min={0}
                          max={24 - settings.width || 0}
                          className="form-control"
                          aria-label="X"
                          aria-describedby="X"
                          onChange={(e) =>
                            handleInputChange(
                              "xPosition",
                              e.target.value,
                              24 - settings.width
                            )
                          }
                          value={settings.xPosition || 0}
                        />
                      </div>
                      <div className="col input-group">
                        <span className="input-group-text border-end-0" id="Y">
                          Y
                        </span>
                        <input
                          name="Y"
                          type="number"
                          className="form-control"
                          aria-label="Y"
                          min={0}
                          max={24 - settings.height || 0}
                          aria-describedby="Y"
                          onChange={(e) =>
                            handleInputChange(
                              "yPosition",
                              e.target.value,
                              24 - settings.height
                            )
                          }
                          value={settings.yPosition || 0}
                        />
                      </div>
                    </div>
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    <div className=" pt-1 me-auto">zIndex</div>
                    <input
                      name="textsizeCity4"
                      type="number"
                      className="form-control w-25 me-4"
                      value={settings.zIndex || 0}
                      onChange={(e) =>
                        handleSettingChange("zIndex", parseInt(e.target.value))
                      }
                    />
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    <div className=" pt-1 me-auto">Label text size</div>
                    <input
                      name="textsizeCity4"
                      type="number"
                      className="form-control w-25 me-4"
                      value={settings.cityNameText || ""}
                      onChange={(e) =>
                        handleSettingChange(
                          "cityNameText",
                          parseInt(e.target.value)
                        )
                      }
                    />
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                    <div className=" pt-1 me-auto">Time text size</div>
                    <input
                      name="textsizeCity5"
                      type="number"
                      className="form-control w-25 me-4"
                      value={settings.cityTimeText || ""}
                      onChange={(e) =>
                        handleSettingChange(
                          "cityTimeText",
                          parseInt(e.target.value)
                        )
                      }
                    />
                  </li>
                  <li className="list-group-item d-flex justify-content-between align-items-center">
                  <div className=" pt-1 pe-1 me-auto">Label:</div>
                  <input
                    name="title"
                    type="text"
                    className="form-control  me-4"
                    value={settings.label || ""}
                    onChange={(e) =>
                      handleSettingChange("label", e.target.value)
                    }
                  />
                </li>
  
                </ol>
              </>
              <div className="accordion accordion-flush " id="accordionClock">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseTwoClock"
                      aria-expanded="true"
                      aria-controls="collapseTwoClock"
                    >
                      Clock background color
                    </button>
                  </h2>
                  <div
                    id="collapseTwoClock"
                    className="accordion-collapse collapse"
                    data-bs-parent="#accordionClock"
                  >
                    <div className="accordion-body">
                      <SketchPicker
                        color={settings.clockBackgroundColor}
                        onChangeComplete={handleOnBackgroundChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseThreeClock"
                      aria-expanded="true"
                      aria-controls="collapseThreeClock"
                    >
                      Clock text color
                    </button>
                  </h2>
                  <div
                    id="collapseThreeClock"
                    className="accordion-collapse collapse"
                    data-bs-parent="#accordionClock"
                  >
                    <div className="accordion-body">
                      <SketchPicker
                        color={settings.textColor}
                        onChangeComplete={handleOnTextChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseFiveClock"
                      aria-expanded="true"
                      aria-controls="collapseFiveClock"
                    >
                      Timezone list
                    </button>
                  </h2>
                  <div
                    id="collapseFiveClock"
                    className="accordion-collapse collapse"
                    data-bs-parent="#accordionClock"
                  >
                    <div className="accordion-body">
                      <div className="select-wrapper">
                        <div className="text-start">Select Timezone:</div>
                        <select
                          id={`timezoneSelector$${id}`}
                          className="form-select"
                          onChange={(e) =>
                            handleTimezoneChange(e.currentTarget.value)
                          }
                        >
                          {options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        className="btn btn-sm btn-success mt-1 w-100"
                        onClick={addClock}
                        disabled={componentInfo.timeZones?.length >= 1}
                      >
                        Add timezone
                      </button>

                      <ol className="list-group list-group-flush text-start mt-1">
                        {componentInfo &&
                        componentInfo.timeZones &&
                        componentInfo.timeZones.length > 0 ? (
                          componentInfo.timeZones.map((item, index) => (
                            <li
                              key={index}
                              className="list-group-item d-flex justify-content-between align-items-start"
                            >
                              <div className="ms-2 me-auto">{item.label}</div>
                              <span
                                className="badge text-bg-danger rounded-0 text-white"
                                onClick={() => removeTimezone(index)}
                                style={{ cursor: "pointer" }}
                              >
                                <i
                                  className="bi bi-trash3"
                                  style={{ fontSize: "14px" }}
                                ></i>
                              </span>
                            </li>
                          ))
                        ) : (
                          <div>NO TIMEZONE ADDED</div>
                        )}
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {deleteConfirmation ? (
                <>
                  <div>Are you sure ?</div>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm rounded-0 "
                    onClick={deleteData}
                    data-bs-dismiss="modal"
                  >
                    YES
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm rounded-0"
                    onClick={() => setDeleteConfirmation(false)}
                  >
                    NO
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm rounded-0 "
                    onClick={() => setDeleteConfirmation(true)}
                  >
                    {" "}
                    REMOVE COMPONENT{" "}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm rounded-0"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Draggable>
  );
}
